import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useCreate, useDelete, useRefresh, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router';
import { AVATAR_RESOURCE_SUPPORTED, FILE_MEDIA_TYPE, INTERVENTION_EXPORT_TAG, MEDIA, MEDIAS } from '@/utils/CONST';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import useGetLastMedia from '@/hooks/useGetLastMedia';
import { getFileBase64 } from '@/utils/files';
import CustomBackdrop from '@/components/common/custom-backdrop/custom-backdrop';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ExportInterventionHeader from '@/components/interventions/export-intervention/sections/header/export-intervention-header';
import ExportInterventionGeneratedDate from '@/components/interventions/export-intervention/sections/generated-date/export-intervention-generated-date';
import ExportInterventionImages from '@/components/interventions/export-intervention/sections/images/export-intervention-images';
import ExportInterventionClientSection from '@/components/interventions/export-intervention/sections/client/export-intervention-client-section';
import ExportInterventionDetailsSection from '@/components/interventions/export-intervention/sections/details/export-intervention-details-section';
import ExportInterventionAdditionalSection from '@/components/interventions/export-intervention/sections/additional/export-intervention-additional-section';
import ExportInterventionSignaturesSection from '@/components/interventions/export-intervention/sections/signatures/export-intervention-signatures-section';
import ExportInterventionDisordersTreatmentSection from '@/components/interventions/export-intervention/sections/disorders-treatment/export-intervention-disorders-treatment-section';

interface ExportInterventionContentProps {
    clientId: string;
    placeId: string;
    interventionId: string;
    onClose: () => void;
}

const ExportInterventionContent = ({ clientId, placeId, interventionId, onClose }: ExportInterventionContentProps) => {
    const translate = useTranslate();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const [, { mutateAsync: deleteMedia }] = useDelete();
    const [backdropStatus, setBackdropStatus] = useState<boolean>(false);
    const refresh = useRefresh();

    const { mediaData: existingPdf, isLoading: isLoadingExistingPdf } = useGetLastMedia({
        resourceId: placeId,
        resourceSupported: AVATAR_RESOURCE_SUPPORTED,
        tagsNames: [INTERVENTION_EXPORT_TAG],
    });

    const inlineImagesAsDataUrl = async (container: HTMLElement) => {
        const imgElements = Array.from(container.querySelectorAll('img'));

        const restoreCallbacks = await Promise.all(
            imgElements.map(async (img) => {
                if (!img.src || img.src.startsWith('data:')) {
                    return undefined;
                }

                const originalSrc = img.src;

                try {
                    const response = await fetch(img.src, {
                        mode: 'cors',
                        credentials: 'include',
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to fetch image with status ${response.status}`);
                    }

                    const blob = await response.blob();

                    const dataUrl = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });

                    img.src = dataUrl;

                    if (!img.complete || img.naturalWidth === 0) {
                        await new Promise<void>((resolve) => {
                            const handleLoad = () => {
                                img.removeEventListener('load', handleLoad);
                                img.removeEventListener('error', handleError);
                                resolve();
                            };

                            const handleError = () => {
                                img.removeEventListener('load', handleLoad);
                                img.removeEventListener('error', handleError);
                                resolve();
                            };

                            img.addEventListener('load', handleLoad);
                            img.addEventListener('error', handleError);
                        });
                    }

                    return () => {
                        img.src = originalSrc;
                    };
                } catch (error) {
                    console.error(`Unable to inline image ${img.src}`, error);
                    return undefined;
                }
            }),
        );

        return () => {
            restoreCallbacks.forEach((callback) => callback?.());
        };
    };

    const generatePdf = async () => {
        setBackdropStatus(true);

        const options = {
            margin: 1,
            filename: 'report.pdf',
            html2canvas: {
                useCORS: true,
                allowTaint: true,
                taintTest: false,
                scale: 3,
                letterRendering: true,
                imageTimeout: 0,
                scrollX: 0,
                scrollY: 0,
            },
            jsPDF: {
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
            },
        };

        const element = document.getElementById('pdf');
        if (!element) {
            console.error('Export container not found');
            setBackdropStatus(false);
            return;
        }

        let restoreImages: (() => void) | undefined;

        try {
            restoreImages = await inlineImagesAsDataUrl(element);
            const worker = html2pdf().set(options).from(element);
            await worker.toCanvas();
            const pdfBlob = await worker.output('blob');
            const base64data = await getFileBase64(pdfBlob);

            const fileUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = 'export.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileUrl);

            setBackdropStatus(false);

            const finalMedia = {
                mediaType: FILE_MEDIA_TYPE,
                resourceSupported: AVATAR_RESOURCE_SUPPORTED,
                fileName: 'export.pdf',
                base64data,
                resourceId: placeId,
                tags: [INTERVENTION_EXPORT_TAG],
                customFields: {
                    tags: [INTERVENTION_EXPORT_TAG],
                },
            };

            const deletePreviousPdf = async () => {
                if (existingPdf?.id) {
                    try {
                        await deleteMedia({
                            resource: MEDIA,
                            id: existingPdf.id,
                        });
                    } catch (error) {
                        console.error('Error removing previous intervention export PDF', error);
                    }
                }
            };

            deletePreviousPdf().finally(() =>
                create(
                    MEDIAS,
                    { data: finalMedia },
                    {
                        onSuccess: () => {
                            refresh();
                            onClose();
                        },
                        onError: () => {},
                    },
                ),
            );
        } catch (error) {
            console.error('Failed to generate intervention export PDF', error);
            setBackdropStatus(false);
        } finally {
            restoreImages?.();
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    width: 900,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                }}
            >
                <Box
                    id='pdf'
                    sx={{
                        display: 'grid',
                        gap: 2,
                        p: 2,
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                >
                    <ExportInterventionHeader />

                    <ExportInterventionGeneratedDate />

                    <ExportInterventionImages interventionId={interventionId} />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 2,
                        }}
                    >
                        <ExportInterventionClientSection clientId={clientId} placeId={placeId} />

                        <ExportInterventionDetailsSection interventionId={interventionId} />
                    </Box>

                    <ExportInterventionAdditionalSection interventionId={interventionId} />

                    <ExportInterventionSignaturesSection interventionId={interventionId} />

                    <ExportInterventionDisordersTreatmentSection interventionId={interventionId} />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    justifyContent: 'center',
                }}
            >
                <LoadingButton
                    variant='contained'
                    size='large'
                    onClick={generatePdf}
                    loading={backdropStatus || isLoadingCreate}
                    disabled={isLoadingExistingPdf}
                >
                    {translate('intervention.export.action')}
                </LoadingButton>
            </Box>

            {(backdropStatus || isLoadingCreate) && (
                <CustomBackdrop
                    message={translate(
                        backdropStatus ? 'messages.creating_pdf' : isLoadingCreate ? 'messages.uploading_pdf' : '',
                    )}
                />
            )}
        </Box>
    );
};

const ExportIntervention = () => {
    const { clientId, placeId, intId } = useParams();
    const navigate = useNavigate();

    if (!clientId || !placeId || !intId) {
        return null;
    }

    return (
        <ExportInterventionContent
            clientId={clientId}
            placeId={placeId}
            interventionId={intId}
            onClose={() => navigate(-1)}
        />
    );
};

export const ExportInterventionDialog = ({
    open,
    onClose,
    clientId,
    placeId,
    interventionId,
}: {
    open: boolean;
    onClose: () => void;
    clientId: string;
    placeId: string;
    interventionId: string;
}) => {
    const translate = useTranslate();

    if (!clientId || !placeId || !interventionId) {
        return null;
    }

    return (
        <DialogTemplate
            open={open}
            onClose={onClose}
            maxWidth='lg'
            title={translate('intervention.export.dialog_title')}
        >
            <ExportInterventionContent
                clientId={clientId}
                placeId={placeId}
                interventionId={interventionId}
                onClose={onClose}
            />
        </DialogTemplate>
    );
};

export default ExportIntervention;
